import { useQuery } from "@apollo/client"
import { useMemo } from "react"
import { GET_PLAYER_STATS } from "@/graphql/queries/playerStats"

export interface PlayerTotals {
  tournaments: number
  points: number
  wins: number
  losses: number
  draws: number
  winRate: number
}

export interface PlayerGameStats {
  name: string
  tournaments: number
  points: number
}

export interface PlayerTournamentTypeStats {
  name: string
  tournaments: number
  points: number
}

export interface DetailedTypeStats {
  name: string
  tournaments: number
  wins: number
  losses: number
  draws: number
  points: number
}

export interface DetailedGameStats {
  name: string
  types: DetailedTypeStats[]
}

export interface AnalyzedSeason {
  id: number
  name: string
  code: string
  startDate: string
  endDate: string
  isActive: boolean
}

export interface GameAnalyzedSeasons {
  name: string
  seasons: AnalyzedSeason[]
}

export interface PlayerIdentity {
  code: string
  name: string
  firstSurname: string
  lastSurname: string
  email: string
  documentNumber: string
}

export interface PlayerStats {
  player: PlayerIdentity
  totals: PlayerTotals
  games: PlayerGameStats[]
  tournamentTypes: PlayerTournamentTypeStats[]
  detailedStats: DetailedGameStats[]
  analyzedSeasons: GameAnalyzedSeasons[]
}

interface PlayerStatsResponse {
  UserStats: {
    player: PlayerIdentity
    totals: PlayerTotals
    games: PlayerGameStats[]
    tournamentTypes: PlayerTournamentTypeStats[]
    detailedStats: DetailedGameStats[]
    analyzedSeasons: GameAnalyzedSeasons[]
    status: {
      success: boolean
      code: string
      detail?: string
      message?: string
    }
  }
}

interface UsePlayerStatsParams {
  personId?: number
  playerCode?: string
  seasonIds?: number[]
  useMock?: boolean
}

// Mock data for development
const mockPlayerStats: PlayerStats = {
  player: {
    code: "MYL-00001",
    name: "Jugador",
    firstSurname: "Demo",
    lastSurname: "Admin",
    email: "demo@example.com",
    documentNumber: "12345678-9",
  },
  totals: {
    tournaments: 57,
    points: 1590,
    wins: 131,
    losses: 50,
    draws: 20,
    winRate: 66.5,
  },
  games: [
    { name: "Mitos y Leyendas", tournaments: 45, points: 1250 },
    { name: "Magic: The Gathering", tournaments: 12, points: 340 },
  ],
  tournamentTypes: [
    { name: "Torneo Abierto", tournaments: 25, points: 620 },
    { name: "Liga Semanal", tournaments: 18, points: 450 },
    { name: "Clasificatorio", tournaments: 10, points: 380 },
    { name: "Nacional", tournaments: 4, points: 140 },
  ],
  detailedStats: [
    {
      name: "Mitos y Leyendas",
      types: [
        { name: "Torneo Abierto", tournaments: 20, wins: 45, losses: 15, draws: 5, points: 520 },
        { name: "Liga Semanal", tournaments: 15, wins: 38, losses: 12, draws: 3, points: 400 },
        { name: "Clasificatorio", tournaments: 8, wins: 20, losses: 8, draws: 2, points: 280 },
        { name: "Nacional", tournaments: 2, wins: 5, losses: 3, draws: 0, points: 50 },
      ],
    },
    {
      name: "Magic: The Gathering",
      types: [
        { name: "Torneo Abierto", tournaments: 5, wins: 10, losses: 5, draws: 1, points: 100 },
        { name: "Liga Semanal", tournaments: 3, wins: 6, losses: 4, draws: 0, points: 50 },
        { name: "Clasificatorio", tournaments: 2, wins: 4, losses: 2, draws: 1, points: 100 },
        { name: "Nacional", tournaments: 2, wins: 3, losses: 5, draws: 0, points: 90 },
      ],
    },
  ],
  analyzedSeasons: [
    {
      name: "Mitos y Leyendas",
      seasons: [
        {
          id: 1,
          name: "Temporada 2025 Apertura",
          code: "A25",
          startDate: "2025-01-01",
          endDate: "2025-06-30",
          isActive: false,
        },
        {
          id: 2,
          name: "Temporada 2025 Clausura",
          code: "C25",
          startDate: "2025-07-01",
          endDate: "2025-12-31",
          isActive: true,
        },
      ],
    },
    {
      name: "Magic: The Gathering",
      seasons: [
        {
          id: 3,
          name: "Temporada 2025",
          code: "T25",
          startDate: "2025-01-01",
          endDate: "2025-12-31",
          isActive: true,
        },
      ],
    },
  ],
}

/**
 * Hook para obtener estadísticas de juego de un jugador.
 * Permite buscar por personId (perfil propio) o playerCode (búsqueda admin).
 * Usa mock data cuando useMock=true (default en desarrollo).
 */
export const usePlayerStats = ({
  personId,
  playerCode,
  seasonIds,
  useMock = false,
}: UsePlayerStatsParams) => {
  const normalizedPlayerCode = playerCode?.trim() || ""
  const normalizedSeasonIds =
    Array.isArray(seasonIds) && seasonIds.length > 0 ? seasonIds : undefined
  const hasPersonId = typeof personId === "number" && personId > 0
  const hasPlayerCode = normalizedPlayerCode.length > 0

  const {
    data,
    loading: queryLoading,
    error: queryError,
    refetch,
  } = useQuery<PlayerStatsResponse>(GET_PLAYER_STATS, {
    variables: {
      input: {
        ...(hasPersonId ? { personId } : {}),
        ...(hasPlayerCode ? { playerCode: normalizedPlayerCode } : {}),
        ...(normalizedSeasonIds ? { seasonIds: normalizedSeasonIds } : {}),
      },
    },
    skip: useMock || (!hasPersonId && !hasPlayerCode),
  })

  if (useMock) {
    return {
      stats: mockPlayerStats,
      loading: false,
      error: null,
      refetch: () => Promise.resolve(),
    }
  }

  const stats = useMemo(() => {
    if (!data?.UserStats?.status?.success) return null

    return {
      player: data.UserStats.player,
      totals: data.UserStats.totals,
      games: data.UserStats.games,
      tournamentTypes: data.UserStats.tournamentTypes,
      detailedStats: data.UserStats.detailedStats,
      analyzedSeasons: data.UserStats.analyzedSeasons,
    }
  }, [data?.UserStats])

  return {
    stats,
    loading: queryLoading,
    error: queryError || null,
    refetch,
  }
}
